// ═══════════════════════════════════════════════════════════
// تعريف جداول الموديولات — المصدر الوحيد لأسماء الأعمدة
// ───────────────────────────────────────────────────────────
// • useSupaTable بيستخدمه للتحويل بين حقول الكود (camelCase) وأعمدة الداتابيز
// • scripts/gen-fix-sql.mjs بيولّد منه supabase/fix.sql
// • scripts/db-test.mjs بيختبر منه إن الداتابيز مطابقة للكود
// أي حقل جديد في الكود لازم يتضاف هنا، وبعدين يتولّد fix.sql من جديد.
//
// صيغة الحقل: key: "type"  ← العمود = snake_case(key)
//             key: ["type", "column"]  ← اسم عمود صريح (للكلمات المحجوزة مثل desc/from/to)
// الأنواع: text | integer | numeric | boolean | date | jsonb
// scope: أعمدة بتفلتر البيانات وبتتضاف تلقائياً مع كل إدخال (مثلاً module)
// managed: false ← جدول موجود من restore.sql أو من supabase/migrations؛ بيتختبر بس مش بيتولّد في fix.sql
// الحذف ناعم (deleted_at) في كل جداول الموديولات — softDelete: false للجداول اللي مالهاش العمود (BUG-048)
// ═══════════════════════════════════════════════════════════

const t = "text", i = "integer", n = "numeric", b = "boolean", d = "date", j = "jsonb";

export const TABLES = {
  // ───── الجاليات ─────
  // preacherId → community_preachers.id (الربط الثابت)؛ preacher = نسخة من الاسم للسجلات القديمة والتصدير
  community_new_muslims: {
    label: "الجاليات — المسلمون الجدد",
    fields: {
      name: t, arabicName: t, islamicName: t, nationality: t, lang: t,
      date: [d, "islam_date"], preacher: t, preacherId: i, phone: t, program: t, status: t,
      educationLevel: t, certificate: t, basicsComplete: b, managerApproved: b,
    },
    required: ["name"],
  },
  community_field_visits: {
    label: "الجاليات — الزيارات الميدانية",
    fields: { place: t, type: t, date: [d, "visit_date"], preacher: t, preacherId: i, lang: t, attendees: i, status: t },
    required: ["place"],
  },

  community_preachers: {
    label: "الجاليات — الدعاة",
    fields: { name: t, lang: t, phone: t, specialty: t, rating: n, userId: i }, // userId → users.id (داشبورد الداعية)
    required: ["name"],
  },
  community_programs: {
    label: "الجاليات — البرامج التعليمية",
    fields: { name: t, type: t, lang: t, students: i, teacher: t, schedule: t },
    required: ["name"],
  },
  community_digital_content: {
    label: "الجاليات — الدعوة الإلكترونية",
    fields: { title: t, type: t, lang: t, views: i, engagement: n },
    required: ["title"],
  },

  // ───── الدعوة والمساجد ─────
  mosques: {
    label: "المساجد — سجل المساجد",
    fields: { name: t, neighborhood: t, type: t, imam: t, phone: t, status: t, events: i },
    required: ["name"],
  },
  mosque_events: {
    label: "المساجد — الأحداث والبرامج",
    fields: {
      mosque: t, type: t, title: t, sheikh: t, date: [d, "event_date"], time: [t, "event_time"],
      attendees: i, status: t, currentStage: i, colorIndex: i, perks: j,
      endDate: d, reportNotes: t, reportAttachment: j, // BUG-033/023 (migration 001)
    },
    required: ["title"],
  },

  mosque_sheikhs: {
    label: "المساجد — المشايخ والدعاة",
    fields: { name: t, phone: t, specialty: t },
    required: ["name"],
  },

  // ───── النسائية ─────
  women_field_programs: {
    label: "النسائية — البرامج الميدانية",
    fields: { title: t, type: t, date: [d, "program_date"], leader: t, attendees: i, beneficiaries: i, status: t },
    required: ["title"],
  },

  women_edu_programs: {
    label: "النسائية — البرامج التعليمية",
    fields: { name: t, type: t, teacher: t, students: i, schedule: t, status: t },
    required: ["name"],
  },
  women_dawah_activities: {
    label: "النسائية — الأنشطة الدعوية",
    fields: { title: t, lang: t, preacher: t, date: [d, "activity_date"], attendees: i, newMuslims: i },
    required: ["title"],
  },
  women_beneficiaries: {
    label: "النسائية — المستفيدات",
    fields: { name: t, nationality: t, type: t, program: t, preacher: t, date: [d, "reg_date"], status: t },
    required: ["name"],
  },

  // ───── الفعاليات ─────
  org_events: {
    label: "الفعاليات",
    fields: {
      title: t, type: t, date: [d, "event_date"], venue: t, attendees: i, budget: n,
      status: t, desc: [t, "description"], recurring: b,
    },
    required: ["title"],
  },

  events_umrah_trips: {
    label: "الفعاليات — رحلات العمرة",
    fields: {
      title: t, date: [d, "trip_date"], pilgrims: i, cost: n, sponsor: t, status: t, notes: t,
    },
    required: ["title"],
  },
  events_bashair: {
    label: "الفعاليات — ملتقيات بشائر الخير",
    fields: {
      title: t, date: [d, "event_date"], venue: t, attendees: i, communities: j, activities: j,
      newMuslims: i, status: t,
    },
    required: ["title"],
  },

  // ───── المالية ─────
  finance_disbursements: {
    label: "المالية — طلبات الصرف",
    fields: {
      title: t, department: t, amount: n, requestedBy: t, date: [d, "request_date"],
      status: t, approvedBy: t, account: t, category: t,
      // fund يتحدد في القاعدة من الحساب؛ paidBy/paidAt يختمهم trigger الصرف (BUG-013/027)
      fund: t, paidBy: t, paidAt: t, paymentRef: t,
    },
    required: ["title"],
  },

  finance_bank_accounts: {
    label: "المالية — الحسابات البنكية",
    fields: { name: t, bank: t, iban: t, type: t, balance: n, fund: t, openingBalance: n },
    required: ["name"],
  },
  finance_budgets: {
    label: "المالية — ميزانيات الإدارات",
    fields: { department: t, year: i, allocated: n },
    required: ["department"],
  },

  // ───── المتبرعين ─────
  donors: {
    label: "المتبرعين — قاعدة المتبرعين",
    fields: {
      name: t, type: t, phone: t, email: t, city: t, totalDonations: n, donationsCount: i,
      lastDonation: d, segment: t, recurring: b, recurringAmount: n, membershipType: t,
      notes: t, donorStatus: t,
    },
    required: ["name"],
  },
  donor_memberships: {
    label: "المتبرعين — العضويات",
    fields: { name: t, type: t, phone: t, fee: n, startDate: d, endDate: d, status: t },
    required: ["name"],
  },

  donor_donations: {
    label: "المتبرعين — سجل التبرعات",
    fields: {
      donorName: t, amount: n, type: t, method: t, account: t, date: [d, "donation_date"], project: t, fund: t,
    },
    required: ["donorName"],
  },
  donor_recurring: {
    label: "المتبرعين — الاستقطاعات المتكررة",
    fields: {
      donorName: t, amount: n, frequency: t, method: t, startDate: d, nextDate: d, status: t, project: t,
    },
    required: ["donorName"],
  },

  // ───── تنمية الموارد ─────
  fundraising_campaigns: {
    label: "تنمية الموارد — الحملات",
    fields: {
      name: t, type: t, audience: i, sent: i, opened: i, converted: i,
      revenue: n, cost: n, date: [d, "campaign_date"], status: t,
    },
    required: ["name"],
  },
  endowments: {
    label: "تنمية الموارد — الأوقاف",
    fields: {
      name: t, type: t, location: t, status: t, manager: t, value: n, annualReturn: n,
      // الإيجارات في endowment_rentals — عمود rentals (JSON) القديم لا يُقرأ ولا يُكتب (يُحذف في migration 010)
      desc: [t, "description"],
    },
    required: ["name"],
  },

  fundraising_visitors: {
    label: "تنمية الموارد — المكاتب التعريفية (الزوار)",
    fields: { name: t, type: t, date: [d, "visit_date"], interest: t, status: t, notes: t },
    required: ["name"],
  },
  fundraising_supporters: {
    label: "تنمية الموارد — الداعمون والمانحون",
    fields: {
      name: t, type: t, partnership: t, amount: n, since: t, status: t, contact: t, phone: t,
    },
    required: ["name"],
  },
  fundraising_library: {
    label: "تنمية الموارد — جرد المكتبة",
    fields: { category: t, lang: t, total: i, distributed: i },
    required: ["category"],
  },

  // ───── الحوكمة ─────
  governance_conflict_declarations: {
    label: "الحوكمة — إقرارات تضارب المصالح",
    fields: {
      member: t, role: [t, "member_role"], year: i, status: t, date: [d, "declared_on"],
      hasConflict: b, details: t,
    },
    required: ["member"],
  },

  org_settings: {
    label: "إعدادات الجمعية (الرؤية والرسالة)",
    fields: { key: [t, "setting_key"], value: [t, "setting_value"] },
    required: ["key"],
  },
  governance_strategic_goals: {
    label: "الحوكمة — الأهداف الاستراتيجية",
    fields: { name: t, kpi: t, target: n, current: n },
    required: ["name"],
  },
  governance_initiatives: {
    label: "الحوكمة — مبادرات الخطة التشغيلية",
    fields: { initiative: t, dept: t, budget: n, timeline: t, progress: i },
    required: ["initiative"],
  },
  governance_regulations: {
    label: "الحوكمة — اللوائح والسياسات",
    fields: { name: t, version: t, lastUpdate: d, status: t, attachment: j },
    required: ["name"],
  },
  governance_financial_statements: {
    label: "الحوكمة — القوائم المالية",
    fields: { name: t, year: i, auditor: t, status: t, date: [d, "statement_date"], attachment: j },
    required: ["name"],
  },
  governance_checklist: {
    label: "الحوكمة — معايير الحوكمة",
    fields: { criterion: t, status: [b, "is_met"] },
    required: ["criterion"],
  },
  governance_licenses: {
    label: "الحوكمة — التراخيص",
    fields: { code: t, type: t, issueDate: d, expiry: d },
    required: ["code"],
  },
  governance_surveys: {
    label: "الحوكمة — قياس الرضا",
    fields: { group: [t, "survey_group"], round: [t, "survey_round"], responses: i, score: n },
    required: ["group"],
  },
  governance_risks: {
    label: "الحوكمة — سجل المخاطر",
    fields: { title: t, category: t, likelihood: t, impact: t, status: t, mitigation: t },
    required: ["title"],
  },

  // ───── الشؤون الإدارية ─────
  hr_employees: {
    label: "الشؤون الإدارية — الموظفين",
    fields: {
      name: t, role: [t, "job_title"], department: t, joinDate: d, salary: n,
      phone: t, email: t, status: t, leaveBalance: i,
    },
    required: ["name"],
  },
  hr_leaves: {
    label: "الشؤون الإدارية — الإجازات",
    fields: {
      employee: t, type: t, from: [d, "from_date"], to: [d, "to_date"], days: i, status: t, reason: t,
      approvedBy: t, // BUG-045 — يختمه trigger الاعتماد
    },
    required: ["employee"],
  },
  hr_purchase_requests: {
    label: "الشؤون الإدارية — طلبات الشراء",
    fields: {
      item: t, department: t, amount: n, requestedBy: t, date: [d, "request_date"], status: t, vendor: t,
    },
    required: ["item"],
  },

  hr_maintenance_tasks: {
    label: "الشؤون الإدارية — الصيانة والنظافة",
    fields: { title: t, type: t, priority: t, assignee: t, date: [d, "task_date"], status: t },
    required: ["title"],
  },
  hr_training: {
    label: "الشؤون الإدارية — التدريب",
    fields: {
      title: t, provider: t, attendees: j, date: [d, "training_date"], duration: t, status: t, cost: n,
    },
    required: ["title"],
  },

  // ───── التطوع ─────
  volunteers: {
    label: "التطوع — المتطوعين",
    fields: {
      name: t, phone: t, skills: t, city: t, hours: n, opportunities: i,
      joined: d, status: t, certified: b,
    },
    required: ["name"],
  },

  volunteer_opportunities: {
    label: "التطوع — الفرص التطوعية",
    fields: { title: t, type: t, slots: i, filled: i, date: [d, "opp_date"], skills: t, status: t },
    required: ["title"],
  },
  volunteer_hours: {
    label: "التطوع — سجل الساعات",
    fields: { volunteer: t, date: [d, "log_date"], hours: n, activity: t },
    required: ["volunteer"],
  },
  volunteer_certificates: {
    label: "التطوع — الشهادات",
    fields: { volunteer: t, type: t, event: t, hours: n, date: [d, "issue_date"], attachment: j },
    required: ["volunteer"],
  },

  // ───── الاتصال المؤسسي ─────
  comms_designs: {
    label: "الاتصال — طلبات التصميم",
    fields: { title: t, type: t, designer: t, date: [d, "design_date"], status: t, revisions: i },
    required: ["title"],
  },
  comms_social_accounts: {
    label: "الاتصال — حسابات التواصل",
    fields: {
      // لا يوجد حقل password — كلمات مرور الحسابات لا تُخزن في النظام (BUG-008)
      platform: t, url: t, username: t, email: t, phone: t, manager: t,
      followers: i, status: t, lastUpdate: d, notes: t, color: t,
    },
    required: ["platform"],
  },
  comms_vendors: {
    label: "الاتصال — الموردين",
    fields: {
      name: t, type: t, serviceType: t, commercialReg: t, nationalId: t, contact: t,
      phone: t, email: t, address: t, contractDetails: t, contractValue: n,
      contractStart: d, contractEnd: d, status: t, achievements: t, rating: n, notes: t,
    },
    required: ["name"],
  },

  comms_pr_events: {
    label: "الاتصال — العلاقات العامة",
    fields: { title: t, type: t, date: [d, "event_date"], status: t, notes: t },
    required: ["title"],
  },
  comms_media_content: {
    label: "الاتصال — المحتوى الإعلامي",
    fields: {
      title: t, type: t, platform: t, date: [d, "publish_date"], views: i, likes: i, shares: i, status: t,
    },
    required: ["title"],
  },
  comms_productions: {
    label: "الاتصال — التصوير والمونتاج",
    fields: {
      title: t, type: t, duration: t, cameraman: t, editor: t, date: [d, "production_date"], status: t,
    },
    required: ["title"],
  },
  comms_website_stats: {
    label: "الاتصال — إحصائيات الموقع الشهرية",
    fields: { date: [d, "period_date"], visitors: i, pageViews: i, bounceRate: n },
    required: ["date"],
  },
  comms_website_pages: {
    label: "الاتصال — مشاهدات صفحات الموقع",
    fields: { page: t, views: i, date: [d, "period_date"] },
    required: ["page"],
  },

  // ───── مجلس الإدارة ─────
  board_committees: {
    label: "مجلس الإدارة — اللجان",
    fields: { name: t, head: t, members: j, tasks: t },
    required: ["name"],
  },

  board_members: {
    label: "مجلس الإدارة — الأعضاء",
    fields: { name: t, role: [t, "member_role"], appointed: d, phone: t },
    required: ["name"],
  },
  board_decisions: {
    label: "مجلس الإدارة — القرارات",
    fields: {
      code: t, text: [t, "decision_text"], date: [d, "decision_date"], meeting: t, responsible: t, status: t,
    },
    required: ["text"],
  },
  board_ceo_evaluation: {
    label: "مجلس الإدارة — تقييم المدير التنفيذي",
    fields: { year: i, criterion: t, score: n },
    required: ["criterion"],
  },

  // ───── تبويبات مشتركة بين الموديولات ─────
  module_meetings: {
    label: "مشترك — الاجتماعات",
    scope: { module: t },
    fields: {
      title: t, date: [d, "meeting_date"], time: [t, "meeting_time"], type: t, status: t,
      attendeesList: j, absent: j, agenda: t, minutes: t, attachment: j, decisions: j,
    },
    required: ["title"],
  },
  module_transactions: {
    label: "مشترك — المعاملات المالية",
    scope: { module: t },
    fields: {
      code: t, date: [d, "tx_date"], type: t, description: t, beneficiary: t, amount: n,
      paymentMethod: t, invoiceNo: t, status: t, attachment: j, notes: t, fund: t,
    },
    required: ["description"],
  },
  operational_plan_programs: {
    label: "مشترك — برامج الخطة التشغيلية",
    scope: { department: t },
    fields: { name: t, cost: n, description: t, goal: t, kpis: j },
    required: ["name"],
  },

  // ───── جداول من supabase/migrations/001 ─────
  new_muslim_followups: {
    label: "الجاليات — متابعات المسلم الجديد",
    managed: false,
    fields: { newMuslimId: i, followupDate: d, type: t, note: t, preacher: t },
    required: ["newMuslimId"],
  },
  endowment_rentals: {
    label: "تنمية الموارد — عقود إيجار الأوقاف",
    managed: false,
    fields: { endowmentId: i, tenant: t, value: n, paymentStatus: t, paymentDate: d, endDate: d },
    required: ["endowmentId", "tenant"],
  },
  // ───── supabase/migrations/012 ─────
  tasks: {
    label: "مشترك — المهام",
    managed: false,
    fields: {
      assignedTo: i, assignedBy: i, title: t, description: t, priority: t, deadline: d, status: t,
      doneAt: t, // timestamptz — للعرض فقط، يضبطه trigger عند الإنجاز
    },
    required: ["assignedTo", "title"],
  },
  // ───── supabase/migrations/014 ─────
  // الأعداد والنسب اللي كانت في الكود (BUG-050) — مفتاح/قيمة؛ التعديل لـ SETTINGS_EDITORS بس، بدون حذف
  app_settings: {
    label: "الإعدادات — الأعداد والنسب",
    managed: false,
    softDelete: false,
    fields: {
      key: [t, "setting_key"], label: t, value: j,
      updatedBy: t, updatedAt: t, // للعرض فقط — يضبطهم trigger trg_app_settings_guard
    },
    required: ["key"],
  },

  // ───── جداول موجودة من restore.sql (بتتختبر بس) ─────
  // users: الأعمدة المسموح قراءتها من الـ API فقط (password_hash غير متاح — BUG-002)؛ التعديل عبر دوال app_admin_*
  users: {
    label: "المستخدمين وتسجيل الدخول",
    managed: false,
    softDelete: false,
    fields: {
      username: t, name: t, email: t, role: t, department: t, phone: t,
      isActive: b, mustChangePassword: b,
    },
    required: ["username"],
  },
  quick_actions: {
    label: "لوحة التحكم — الإجراءات السريعة",
    managed: false,
    softDelete: false,
    fields: { actionType: t, data: j },
    required: ["actionType"],
  },
  notifications: {
    label: "الإشعارات والتكليفات",
    managed: false,
    softDelete: false,
    fields: { title: t, body: t, type: t, isRead: b },
    required: ["title"],
  },
  audit_log: {
    label: "سجل التدقيق",
    managed: false,
    softDelete: false,
    fields: { username: t, action: t, details: t, module: t },
    required: ["action"],
  },
};

// الجدول فيه عمود deleted_at؟ (الحذف من الواجهة = تعليم وليس حذفاً فعلياً)
export const hasSoftDelete = (table) => TABLES[table]?.softDelete !== false;

export const snake = (s) => s.replace(/[A-Z]/g, (c) => "_" + c.toLowerCase());

// كل الأعمدة لجدول: [{ key, column, type }]
export const columnsOf = (table) => {
  const def = TABLES[table];
  if (!def) throw new Error(`جدول غير معرّف في dbSchema: ${table}`);
  const cols = [];
  Object.entries(def.scope || {}).forEach(([key, type]) => cols.push({ key, column: key, type, scope: true }));
  Object.entries(def.fields).forEach(([key, spec]) => {
    const [type, column] = Array.isArray(spec) ? spec : [spec, snake(key)];
    cols.push({ key, column, type });
  });
  return cols;
};

// ───── حسابات التواصل بدون عمود كلمة المرور (BUG-008) — لأي شاشة عرض/لوحة تحكم ─────
export const SOCIAL_ACCOUNTS_SAFE_SELECT = ["id", "created_at", "updated_at",
  ...columnsOf("comms_social_accounts").map((c) => c.column).filter((c) => c !== "password")].join(",");

// ───── تحويل القيم لنوع العمود ─────
const pad = (x) => String(x).padStart(2, "0");

export const toISODate = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  // تاريخ بالتوقيت المحلي وليس UTC (BUG-044)
  if (v instanceof Date) return isNaN(v) ? undefined : `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  const s = String(v).trim();
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (m) {
    let [a, c] = [Number(m[1]), Number(m[2])];
    // الصيغة السعودية يوم/شهر افتراضياً؛ لو الرقم الثاني > 12 يبقى شهر/يوم (BUG-029)
    const [day, month] = c > 12 && a <= 12 ? [c, a] : [a, c];
    const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return `${year}-${pad(month)}-${pad(day)}`;
  }
  return undefined; // غير صالح
};

const coerce = (value, type, key, table) => {
  if (value === undefined) return undefined;
  switch (type) {
    case "integer":
    case "numeric": {
      if (value === "" || value === null) return null;
      const num = Number(String(value).replace(/[٬,\s]/g, ""));
      if (isNaN(num)) throw new Error(`قيمة رقمية غير صالحة "${value}" في الحقل ${key} (${table})`);
      return type === "integer" ? Math.round(num) : num;
    }
    case "boolean": {
      if (value === "" || value === null) return null;
      if (typeof value === "boolean") return value;
      // النص "false" أو "لا" أو "0" = false وليس true (BUG-057)
      const t = String(value).trim().toLowerCase();
      if (["false", "0", "no", "n", "لا", "خطأ", "غير"].includes(t)) return false;
      if (["true", "1", "yes", "y", "نعم", "صح", "✓"].includes(t)) return true;
      return Boolean(value);
    }
    case "date": {
      const iso = toISODate(value);
      if (iso === undefined) throw new Error(`تاريخ غير صالح "${value}" في الحقل ${key} (${table}) — استخدم الصيغة YYYY-MM-DD`);
      return iso;
    }
    case "jsonb":
      return value === "" ? null : value;
    default:
      return value === null ? null : String(value);
  }
};

// كائن من الكود → صف للداتابيز (أي حقل مش معرّف في dbSchema بيتجاهل)
export const toRow = (table, obj, scope = {}) => {
  const row = {};
  columnsOf(table).forEach(({ key, column, type, scope: isScope }) => {
    const src = isScope ? scope : obj;
    if (!(key in src)) return;
    const v = coerce(src[key], type, key, table);
    if (v !== undefined) row[column] = v;
  });
  return row;
};

// صف من الداتابيز → كائن للكود
export const fromRow = (table, row) => {
  const out = { id: row.id, createdAt: row.created_at };
  columnsOf(table).forEach(({ key, column }) => {
    if (column in row) out[key] = row[column];
  });
  return out;
};
